// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  Button,
  Column,
  Description,
  InlineAlert,
  LoadingEmptyState,
  MdSpacing,
  SecondaryPage,
  XlPadding,
  XlSpacing,
} from '@alto/design-system';
import { InsuranceCard } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { Fragment, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { setEditingInsuranceID } from '~shared/actions/onboarding';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared } from '~shared/store';
import type { Insurance as InsuranceType } from '~shared/types';
import { InfoRequestHeader } from './InfoRequestHeader';
import { type HubReferralRouteParams } from './LandingPage';
import { NavBarLogo } from './NavBarLogo';
import { useQueryInsurancePageContent } from '~web/features/info-request/hooks';
import { useStatusBarColor } from '~web/features/info-request/hooks/useStatusBarColor';

export const InsurancePage = () => {
  const { goBack, navigate } = useNavigation();
  const { hubReferralID: hubReferralIDParam } = useParams<HubReferralRouteParams>();
  const hubReferralID = Number(hubReferralIDParam || 0);
  const dispatch = useDispatchShared();
  const { trackPageView } = useAnalytics();
  const currentPath = window.location.pathname;
  const { insurances, isLoading: fetchInsurancesLoading, error: fetchInsurancesError } = useGetInsurances();
  const { isFetching, headerLogoUrl, nextURL, redirectURL } = useQueryInsurancePageContent({
    hubReferralID,
  });

  const handleEditInsurance = (insurance: InsuranceType) => {
    const uploadPhotoURL = currentPath.replace('/insurance/', '/insurance-input-photo/');
    const enterManualURL = currentPath.replace('/insurance/', '/insurance-input-manual/');
    const url = insurance.image_url ? uploadPhotoURL : enterManualURL;
    dispatch(setEditingInsuranceID(insurance.id));
    navigate(url);
  };

  const handleSubmit = () => {
    if (nextURL) {
      navigate(nextURL);
    }
  };

  useStatusBarColor(COLORS.BACKGROUND_COLORS.GREY_LIGHTEST);

  useEffect(() => {
    if (hubReferralID) {
      trackPageView({
        event: EVENTS.INFO_REQUEST__INSURANCE_FOUND_VIEWED,
        params: { origin: '/insurance-input', hub_referral_id: hubReferralID, next_url: nextURL },
      });
    }
  }, [hubReferralID, trackPageView, nextURL]);

  useEffect(() => {
    if (redirectURL) {
      navigate(redirectURL);
    }
  }, [navigate, redirectURL]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      footerPlacementContext="webScreenWithoutPolicyFooter"
      withoutWebFramingElementHeights
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      dismissIcon="chevronleft"
      onDismiss={goBack}
      NavBarCenterContent={headerLogoUrl ? <NavBarLogo illustrationSrc={headerLogoUrl} /> : undefined}
      HeaderContent={
        <InfoRequestHeader
          title="Insurance"
          subtitle="Review the info below to make sure it's correct."
        />
      }
      buttons={
        !fetchInsurancesError && !fetchInsurancesLoading && !isFetching
          ? [
              <Button
                label="Continue"
                key="continue"
                onPress={handleSubmit}
              />,
            ]
          : []
      }
    >
      {fetchInsurancesLoading ? <LoadingEmptyState /> : null}
      {fetchInsurancesError && !fetchInsurancesLoading ? (
        <>
          <InlineAlert type="error">
            <Description>Looks like we're having some technical difficulty. Please refresh this page.</Description>
          </InlineAlert>
          <XlSpacing />
        </>
      ) : null}
      {!fetchInsurancesLoading && !fetchInsurancesError && (
        <Column>
          {insurances.map((insurance, index) => (
            <Fragment key={`insurance-card-${insurance.id}`}>
              <InsuranceCard
                insurance={insurance}
                onEditInsurance={handleEditInsurance}
              />
              {index !== insurances.length - 1 && <MdSpacing />}
            </Fragment>
          ))}
        </Column>
      )}
    </SecondaryPage>
  );
};
